import React, { useState } from "react";
import { FiUsers, FiClock, FiMapPin, FiX, FiExternalLink, FiUserPlus } from "react-icons/fi";
import "./GoogleCalendarView.css";

const GoogleCalendarView = ({ events, followUps = [], onFollowUpClick, emailPeopleMap = {}, onAddPerson, onViewPerson }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Always show 7 AM - 6 PM
  const calculateTimeRange = () => {
    return { startHour: 7, endHour: 18 };
  };
  
  const { startHour, endHour } = calculateTimeRange();
  
  const timeSlots = [];
  
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minutes = 0; minutes < 60; minutes += 15) {
      if (hour === endHour && minutes > 0) break; // Stop at the end hour
      timeSlots.push({ hour, minutes });
    }
  }

  const formatTimeSlot = (hour, minutes) => {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return minutes === 0 ? `${displayHour} ${period}` : "";
  };

  // Detect overlapping events
  const detectOverlaps = (events) => {
    const sortedEvents = [...events].sort((a, b) => {
      const aStart = new Date(a.start?.dateTime || a.start?.date);
      const bStart = new Date(b.start?.dateTime || b.start?.date);
      return aStart - bStart;
    });

    const columns = [];
    
    sortedEvents.forEach(event => {
      const eventStart = new Date(event.start?.dateTime || event.start?.date);
      const eventEnd = new Date(event.end?.dateTime || event.end?.date);
      
      // Find a column where this event doesn't overlap
      let placed = false;
      for (let i = 0; i < columns.length; i++) {
        const column = columns[i];
        const overlaps = column.some(e => {
          const eStart = new Date(e.start?.dateTime || e.start?.date);
          const eEnd = new Date(e.end?.dateTime || e.end?.date);
          return eventStart < eEnd && eventEnd > eStart;
        });
        
        if (!overlaps) {
          column.push(event);
          event._column = i;
          placed = true;
          break;
        }
      }
      
      if (!placed) {
        columns.push([event]);
        event._column = columns.length - 1;
      }
      
      // Calculate how many columns this event overlaps with
      const overlappingColumns = columns.filter(column => {
        return column.some(e => {
          const eStart = new Date(e.start?.dateTime || e.start?.date);
          const eEnd = new Date(e.end?.dateTime || e.end?.date);
          return eventStart < eEnd && eventEnd > eStart;
        });
      });
      
      event._totalColumns = Math.max(overlappingColumns.length, event._column + 1);
    });
    
    // Update all events with their max column count
    sortedEvents.forEach(event => {
      const eventStart = new Date(event.start?.dateTime || event.start?.date);
      const eventEnd = new Date(event.end?.dateTime || event.end?.date);
      
      const overlappingEvents = sortedEvents.filter(e => {
        const eStart = new Date(e.start?.dateTime || e.start?.date);
        const eEnd = new Date(e.end?.dateTime || e.end?.date);
        return eventStart < eEnd && eventEnd > eStart;
      });
      
      const maxColumns = Math.max(...overlappingEvents.map(e => (e._column || 0) + 1));
      event._totalColumns = maxColumns;
    });
    
    return sortedEvents;
  };

  // Calculate position and height for each event
  const getEventStyle = (event) => {
    const startTime = event.start?.dateTime || event.start?.date;
    const endTime = event.end?.dateTime || event.end?.date;
    
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    
    // Calculate minutes from start of day
    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
    const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();
    const durationMinutes = endMinutes - startMinutes;
    
    // Calculate position relative to start hour
    const gridStartMinutes = startHour * 60;
    const topPosition = ((startMinutes - gridStartMinutes) / 15) * 20; // 20px per 15-minute slot
    const height = (durationMinutes / 15) * 20;
    
    // Add spacing at top and bottom (1px each for smaller size)
    const spacing = 2;
    
    // Handle overlapping events
    const column = event._column || 0;
    const totalColumns = event._totalColumns || 1;
    const widthPercent = 100 / totalColumns;
    const leftPercent = widthPercent * column;
    
    return {
      top: `${topPosition + 1}px`, // 1px top spacing
      height: `${Math.max(height - spacing, 18)}px`, // 2px total spacing (1px top + 1px bottom), min 18px
      left: `${leftPercent}%`,
      width: `calc(${widthPercent}% - 4px)`, // 4px margin between overlapping events
    };
  };

  // Google Calendar color mapping
  const googleCalendarColors = {
    '1': '#a4bdfc', // Lavender
    '2': '#7ae7bf', // Sage
    '3': '#dbadff', // Grape
    '4': '#ff887c', // Flamingo
    '5': '#fbd75b', // Banana
    '6': '#ffb878', // Tangerine
    '7': '#46d6db', // Peacock
    '8': '#e1e1e1', // Graphite
    '9': '#5484ed', // Blueberry
    '10': '#51b749', // Basil
    '11': '#dc2127', // Tomato
  };

  const getEventColor = (event) => {
    const now = new Date();
    const endTime = event.end?.dateTime || event.end?.date;
    const isPast = new Date(endTime) < now;
    
    // Check if it's a meeting (>1 attendee) or task (0-1 attendees)
    const attendeeCount = event.attendees?.length || 0;
    const isMeeting = attendeeCount > 1;
    
    // Determine the base color
    let baseColor;
    
    // If user has set a custom color, use it
    if (event.backgroundColor) {
      baseColor = event.backgroundColor;
    } else if (event.colorId && googleCalendarColors[event.colorId]) {
      baseColor = googleCalendarColors[event.colorId];
    } else {
      // Default colors: green for meetings, blue for tasks
      baseColor = isMeeting ? '#51b749' : '#4285f4';
    }
    
    // If past, make it lighter by converting to rgba with 0.4 opacity
    if (isPast) {
      const hex = baseColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const lighterColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
      return lighterColor;
    }
    
    return baseColor;
  };

  const formatEventTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="google-calendar-view">
      <div className="calendar-grid">
        {/* Time column */}
        <div className="time-column">
          {timeSlots.map((slot, index) => (
            <div key={index} className="time-slot">
              <span className="time-label">{formatTimeSlot(slot.hour, slot.minutes)}</span>
            </div>
          ))}
        </div>

        {/* Events column */}
        <div className="events-column">
          {/* Grid lines */}
          <div className="grid-lines">
            {timeSlots.map((slot, index) => (
              <div 
                key={index} 
                className={`grid-line ${slot.minutes === 0 ? 'hour-line' : ''}`}
              />
            ))}
          </div>

          {/* Events */}
          <div className="events-container">
            {detectOverlaps(events).map(event => {
              const startTime = event.start?.dateTime || event.start?.date;
              const endTime = event.end?.dateTime || event.end?.date;
              const eventStyle = getEventStyle(event);
              const eventColor = getEventColor(event);
              const now = new Date();
              const isNow = new Date(startTime) <= now && new Date(endTime) > now;

              return (
                <div
                  key={event.id}
                  className={`calendar-event ${isNow ? 'happening-now' : ''}`}
                  style={{
                    ...eventStyle,
                    backgroundColor: eventColor,
                  }}
                  onClick={() => setSelectedEvent(event)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setSelectedEvent(event);
                  }}
                >
                  <div className="event-content">
                    <div className="event-header">
                      <div className="event-title">{event.summary || "Untitled Event"}</div>
                      {event.attendees && event.attendees.length > 0 && (
                        <div className="event-attendees-inline">
                          <FiUsers size={10} /> {event.attendees.length}
                        </div>
                      )}
                    </div>
                    <div className="event-time-range">
                      {formatEventTime(startTime)} - {formatEventTime(endTime)}
                    </div>
                    {event.location && (
                      <div className="event-location">📍 {event.location}</div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Follow-ups */}
            {followUps.map(followUp => {
              if (!followUp.follow_up_date) return null;
              
              const followUpDate = new Date(followUp.follow_up_date);
              const followUpMinutes = followUpDate.getHours() * 60 + followUpDate.getMinutes();
              const gridStartMinutes = startHour * 60;
              const gridEndMinutes = endHour * 60;
              
              // Only show if within grid hours
              if (followUpMinutes < gridStartMinutes || followUpMinutes > gridEndMinutes) {
                return null;
              }
              
              const topPosition = ((followUpMinutes - gridStartMinutes) / 15) * 20;
              const now = new Date();
              const isPast = followUpDate < now;
              
              return (
                <div
                  key={`followup-${followUp.id}`}
                  className={`calendar-followup ${isPast ? 'past' : ''}`}
                  style={{
                    top: `${topPosition + 1}px`,
                    height: '8px', // ~5 minutes scaled down
                  }}
                  title={`Follow-up: ${followUp.name}${followUp.company ? ` (${followUp.company})` : ''}`}
                  onClick={() => onFollowUpClick && onFollowUpClick(followUp)}
                >
                  <div className="followup-indicator">📌</div>
                  <div className="followup-name">{followUp.name}</div>
                </div>
              );
            })}
          </div>

          {/* Current time indicator */}
          {(() => {
            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            const gridStartMinutes = startHour * 60;
            const gridEndMinutes = endHour * 60;
            
            if (currentMinutes >= gridStartMinutes && currentMinutes <= gridEndMinutes) {
              const topPosition = ((currentMinutes - gridStartMinutes) / 15) * 20;
              
              return (
                <div className="current-time-indicator" style={{ top: `${topPosition}px` }}>
                  <div className="time-indicator-dot" />
                  <div className="time-indicator-line" />
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="event-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-modal-header">
              <h3>{selectedEvent.summary || "Untitled Event"}</h3>
              <button className="close-modal" onClick={() => setSelectedEvent(null)}>
                <FiX size={20} />
              </button>
            </div>
            <div className="event-modal-body">
              <div className="event-detail">
                <FiClock size={16} />
                <div>
                  <div className="detail-label">Time</div>
                  <div className="detail-value">
                    {formatEventTime(selectedEvent.start?.dateTime || selectedEvent.start?.date)} - {formatEventTime(selectedEvent.end?.dateTime || selectedEvent.end?.date)}
                  </div>
                  <div className="detail-date">
                    {new Date(selectedEvent.start?.dateTime || selectedEvent.start?.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>
              </div>

              {selectedEvent.location && (
                <div className="event-detail">
                  <FiMapPin size={16} />
                  <div>
                    <div className="detail-label">Location</div>
                    <div className="detail-value">{selectedEvent.location}</div>
                  </div>
                </div>
              )}

              {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                <div className="event-detail">
                  <FiUsers size={16} />
                  <div>
                    <div className="detail-label">Attendees ({selectedEvent.attendees.length})</div>
                    <div className="attendees-list">
                      {selectedEvent.attendees.map((attendee, idx) => {
                        const normalizedEmail = attendee.email?.toLowerCase().trim();
                        const personExists = normalizedEmail && emailPeopleMap[normalizedEmail];
                        
                        // Extract name from displayName or email
                        const attendeeName = attendee.displayName || attendee.email?.split('@')[0] || '';
                        
                        return (
                          <div key={idx} className="attendee-item">
                            <div className="attendee-info">
                              <span className="attendee-email">
                                {attendee.displayName && <strong>{attendee.displayName}</strong>}
                                {attendee.displayName && <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: '4px' }}>({attendee.email})</span>}
                                {!attendee.displayName && attendee.email}
                              </span>
                              <div className="attendee-badges">
                                {attendee.organizer && <span className="attendee-badge">Organizer</span>}
                                {attendee.responseStatus === 'accepted' && <span className="response-badge accepted">✓</span>}
                                {attendee.responseStatus === 'declined' && <span className="response-badge declined">✗</span>}
                                {attendee.responseStatus === 'tentative' && <span className="response-badge tentative">?</span>}
                              </div>
                            </div>
                            <div className="attendee-actions">
                              {personExists ? (
                                <button
                                  className="attendee-action-btn view"
                                  onClick={() => onViewPerson && onViewPerson(personExists)}
                                  title="View details"
                                >
                                  <FiExternalLink size={14} />
                                </button>
                              ) : (
                                <button
                                  className="attendee-action-btn add"
                                  onClick={() => onAddPerson && onAddPerson({ email: attendee.email, name: attendeeName })}
                                  title="Add to people"
                                >
                                  <FiUserPlus size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {selectedEvent.description && (
                <div className="event-detail">
                  <div>
                    <div className="detail-label">Description</div>
                    <div className="detail-value" dangerouslySetInnerHTML={{ __html: selectedEvent.description }} />
                  </div>
                </div>
              )}

              {selectedEvent.htmlLink && (
                <div className="event-detail">
                  <a 
                    href={selectedEvent.htmlLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="calendar-link"
                  >
                    <FiExternalLink size={16} />
                    Open in Google Calendar
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarView;

